import { cn } from '@/lib/utils';
import { CONTRACT_TYPE_OPTIONS, CONTRACT_TYPE_HINTS } from '@/pages/contracts/contractTypeSchema';

interface ContractTypeSelectorProps {
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Required contract-type picker for the upload form.
 *
 * The declared type is not cosmetic — it scopes which gate rules run, which
 * clauses are treated as missing, and which legal bases are looked up per
 * jurisdiction. The Document Agent still infers a type independently; a
 * disagreement surfaces as a gate warning rather than silently overriding the
 * uploader.
 *
 * Implemented as a native radiogroup (not a listbox) so the whole set is
 * visible at once with its hints, and so arrow-key navigation works without a
 * roving-tabindex implementation of our own.
 */
export function ContractTypeSelector({
  value,
  onChange,
  disabled = false,
}: ContractTypeSelectorProps) {
  return (
    <fieldset disabled={disabled} className="space-y-2">
      <legend className="block text-sm font-medium text-foreground">
        Contract type
        <span className="ml-1 text-destructive" aria-hidden="true">*</span>
      </legend>
      <p className="text-sm text-muted-foreground">
        Determines which gate rules, expected clauses, and jurisdiction legal bases apply.
      </p>
      <div
        role="radiogroup"
        aria-label="Contract type"
        aria-required="true"
        className="grid gap-2 sm:grid-cols-2"
      >
        {CONTRACT_TYPE_OPTIONS.map((option) => {
          const isSelected = value === option.value;
          return (
            <label
              key={option.value}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors',
                'hover:bg-accent/50 focus-within:ring-2 focus-within:ring-ring',
                isSelected ? 'border-primary bg-accent' : 'border-input',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <input
                type="radio"
                name="declared_contract_type"
                value={option.value}
                checked={isSelected}
                onChange={() => { onChange(option.value); }}
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-foreground">{option.label}</span>
                <span className="block text-xs text-muted-foreground">
                  {CONTRACT_TYPE_HINTS[option.value]}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
